import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import AddIcon from "@material-ui/icons/AddOutlined";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useAlert } from "react-alert";
import uuidv4 from "uuid/v4";

import { makeRequest } from "../../util";
import { useModalStyles } from "./styles";

export function makeSaveFunction(
  dest,
  settings,
  values,
  onSave,
  handleClose,
  alert
) {
  const handleSave = () => {
    const id = uuidv4();

    const settingsToSave = _(settings)
      .extend(values)
      // Remove any values that are just a blank string as that's a filler value
      // we use from the backend.
      .pickBy((value) => value !== "")
      .value();

    const destConfig = {
      id: id,
      dest_type: dest.key,
      name: dest.key,
      connection_mode: dest.connectionMode,
      settings: settingsToSave,
    };

    makeRequest("save_destination_config", destConfig)
      .then(() => {
        onSave && onSave(dest, id, destConfig);
        handleClose();
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to save destination.");
        console.log(error);
        Sentry.captureException(error);
      });
  };
  return handleSave;
}

const ModalField = ({
  field,
  values,
  settings,
  handleFieldChange,
  CustomConfigControlComponent,
}) => {
  const classes = useModalStyles();
  const defaultValue = field.defaultValue || null;
  var value;

  if (field.sensitive) {
    if (field.key in values) {
      value = values[field.key];
    } else if (field.key in settings) {
      // If the user hasn't edited the field, but there is an existing value
      // for it, show a bunch of asterisks.
      value = "********";
    } else {
      value = "";
    }
  } else {
    value =
      values[field.key] === undefined || values[field.key] === null
        ? settings[field.key]
        : values[field.key];

    if (value === undefined || value === null) {
      value = defaultValue;
    }
  }

  if (CustomConfigControlComponent) {
    return (
      <FormControl
        component="fieldset"
        className={clsx("my-5", "w-full", classes.formControl)}
      >
        <FormGroup>
          <FormControlLabel
            className={clsx(classes.formControlLabel)}
            control={
              <CustomConfigControlComponent
                field={field}
                value={value}
                onChange={handleFieldChange}
              />
            }
          />
        </FormGroup>
        {field.description && (
          <FormHelperText>
            <div className="text-xs text-gray-600 leading-4 -mt-1">
              {field.description}
            </div>
          </FormHelperText>
        )}
      </FormControl>
    );
  }

  if (field.type && field.type === "boolean") {
    return (
      <FormControl
        component="fieldset"
        className={clsx("my-5", "w-full", classes.formControl)}
      >
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                className="m-1"
                checked={value}
                onChange={(event) =>
                  handleFieldChange(event.target.name, event.target.checked)
                }
                name={field.key}
              />
            }
            label={field.name}
          />
        </FormGroup>
        {field.description && (
          <FormHelperText component="div">
            <div className="text-xs text-gray-600 leading-4 -mt-1">
              {field.description}
            </div>
          </FormHelperText>
        )}
      </FormControl>
    );
  }

  return (
    <FormControl
      component="fieldset"
      className={clsx("my-5", "w-full", classes.formControl)}
    >
      <FormGroup>
        <FormControlLabel
          className={clsx(classes.formControlLabel)}
          control={
            <TextField
              fullWidth
              autoComplete="off"
              label={field.name}
              name={field.key}
              onChange={(event) =>
                handleFieldChange(event.target.name, event.target.value)
              }
              // If the user previously configured this field we want to display
              // asterisks when they go to configure it.
              value={value || ""}
              key={field.name}
              variant="outlined"
              type={field.type || "text"}
              className={clsx(classes.inputField, "mb-2 fs-exclude")}
            />
          }
        />
      </FormGroup>
      {field.description && (
        <FormHelperText component="div">
          <div className="text-xs text-gray-600 leading-4 -mt-1">
            {field.description}
          </div>
        </FormHelperText>
      )}
    </FormControl>
  );
};

const ConfigureDestinationsModal = ({
  open,
  onClose,
  onSave,
  dest,
  includeHeader = false,
  settings,
  limitSettings = null,
  settingsToControl = {},
  title = "Integration",
  configFields = dest.configFields,
}) => {
  // Ok, pay attention, this is tricky.
  // `settings` is the settings sent from our server. This usually does not include any sensitive information (i.e. secret keys)
  // `defaultValueMap` is a base layer of default values that comes from the destination config
  // `values` (defined later) is meant to be the current state of values that we show to the users.
  //
  // Where this keys tricky is sensitive values... how do we avoid showing them & sending them from the server in a
  // way that we still know when it's already been filled in and can be replaced?
  //
  // The way we do this is by sending an empty string, and any field marked as "sensitive" in the dest.configFields will
  //  be treated differently
  // First thing we do is filter those values out of the `settings` into `nonSensitiveSettings` object.
  // So now we have `nonSensitiveSettings` which is the values that are safe to display to the user, and `settings`
  //  which will differ from `nonSensitiveSettings` only in the sensitive values.
  //
  // We initialize `values` with the `nonSensitiveSettings` layered on top of the `defaultValueMap`
  // To the field component, we pass all of these in.
  // That component will attempt to display in order:
  // - If the field is sensitive & in `values`, the user has entered it after page load, so we display it as-is.
  // - If the field is sensitive & not in `values` & is in `settings`, this means it's been set, so display something like "***"
  // - If the field is sensitive & not in `values` & not in `settings`, it hasn't been set

  const defaultValueMap = Object.fromEntries(
    dest.configFields
      .filter((field) => field.defaultValue !== undefined)
      .map((field) => {
        let value;
        switch (field.type) {
          case "number":
            value = parseInt(field.defaultValue);
            break;
          case "boolean":
            value = !!field.defaultValue || false;
            break;
          case "keyValueList":
            value = [];
            break;
          case "list":
            value = [];
            break;
          default:
            value = field.defaultValue || "";
        }

        return [field.key, value];
      })
  );

  const nonSensitiveSettings = Object.fromEntries(
    Object.keys(settings)
      .filter((fieldKey) => {
        const fieldConfig = dest.configFields.find((f) => f.key === fieldKey);
        if (!fieldConfig) {
          return false;
        }
        return !fieldConfig.sensitive;
      })
      .map((fieldKey) => [fieldKey, settings[fieldKey]])
  );

  const classes = useModalStyles();
  const alert = useAlert();
  const [values, setValues] = useState({
    ...defaultValueMap,
    ...nonSensitiveSettings,
  });

  const handleFieldChange = (key, value) => {
    setValues((currentValues) => ({
      ...defaultValueMap,
      ...currentValues,
      [key]: value,
    }));
  };

  const handleClose = () => {
    setValues({});
    onClose();
  };

  const handleSave = makeSaveFunction(
    dest,
    settings,
    values,
    onSave,
    handleClose,
    alert
  );

  if (!open) {
    return null;
  }

  const renderableFields = configFields.filter((field) => {
    return !limitSettings || limitSettings.indexOf(field.key) !== -1;
  });

  const textFields = renderableFields
    .filter((field) => {
      if (field.enabledIfCheckbox) {
        return !!values[field.enabledIfCheckbox];
      }
      return true;
    })
    .map((field, i) => (
      <ModalField
        key={i}
        field={field}
        values={values}
        settings={settings}
        handleFieldChange={handleFieldChange}
        CustomConfigControlComponent={settingsToControl[field.key]}
      />
    ));

  return (
    <Modal onClose={handleClose} open={open}>
      <Card className={clsx(classes.root, classes.smallModal)}>
        <CardHeader
          title={`${title} Settings: ${dest.name}`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          {includeHeader && (
            <Typography variant="body1">{dest.configHelp}</Typography>
          )}
          {textFields}
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={handleClose}>Close</Button>
          {onSave && (
            <Button color="primary" onClick={handleSave} variant="contained">
              Save
            </Button>
          )}
        </CardActions>
      </Card>
    </Modal>
  );
};

ConfigureDestinationsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  dest: PropTypes.object,
  configFields: PropTypes.array,
  includeHeader: PropTypes.bool,
  settings: PropTypes.object.isRequired,
  limitSettings: PropTypes.arrayOf(PropTypes.string),
};

const KeyValueMappingControl = ({
  field,
  value: mappingValues = [],
  onChange,
  keyLabel,
  valueLabel,
  objectLabel,
}) => {
  const classes = useModalStyles();

  const onMappingChange = (index, { key, value }) => {
    const newValue = mappingValues.slice();
    newValue[index] = _.merge(
      { key: mappingValues[index].key, value: mappingValues[index].value },
      { key, value }
    );
    onChange(field.key, newValue);
  };

  const onDelete = (index) => {
    const newValue = mappingValues.slice();
    newValue.splice(index, 1);
    onChange(field.key, newValue);
  };

  return (
    <div className="justify-items-stretch" style={{ width: "100%" }}>
      {mappingValues.map((kvPair, index) => {
        return (
          <div className="flex items-stretch" key={index}>
            <span className="flex justify-start-items-center">
              <TextField
                fullWidth
                autoComplete="off"
                label={keyLabel}
                name={keyLabel}
                onChange={(event) =>
                  onMappingChange(index, { key: event.target.value })
                }
                value={kvPair.key || ""}
                key={field.name}
                variant="outlined"
                type={"text"}
                inputProps={{ style: { fontFamily: "monospace" } }}
                className={clsx(classes.inputField, "mb-2 fs-exclude")}
              />
            </span>
            <span className="flex items-center justify-self-center">
              <ArrowRightIcon />
            </span>
            <span className="flex justify-start-items-center">
              <TextField
                fullWidth
                autoComplete="off"
                label={valueLabel}
                name={valueLabel}
                onChange={(event) =>
                  onMappingChange(index, { value: event.target.value })
                }
                value={kvPair.value || ""}
                key={field.name}
                variant="outlined"
                type={"text"}
                inputProps={{ style: { fontFamily: "monospace" } }}
                className={clsx(classes.inputField, "mb-2 fs-exclude")}
              />
            </span>
            <span className="flex flex-wrap justify-end">
              <Tooltip title={"Delete " + objectLabel}>
                <div
                  className="hover:bg-navy-200 p-1 rounded-full flex justify-center items-center"
                  onClick={() => onDelete(index)}
                >
                  <CloseIcon className={classes.closeIcon} />
                </div>
              </Tooltip>
            </span>
          </div>
        );
      })}
      <Button
        color="primary"
        className="text-xs bg-navy-100 text-navy-800 inline-block inline-flex items-center px-3 py-1 rounded cursor-pointer hover:bg-navy-200"
        onClick={() =>
          onChange(field.key, [...mappingValues, { key: "", value: "" }])
        }
      >
        <AddIcon className="mr-1" style={{ width: 16 }} />
        Add {objectLabel}
      </Button>
    </div>
  );
};

export { ConfigureDestinationsModal, KeyValueMappingControl };
